import React from "react";
import "./style.css";
import Navbar from "../../Components/Navbar";

const Privacy = () => {
  return (
    <>
      <Navbar />
      <div className="privacy-container">
        <div className="privacy-content">
          <h1 className="title">Privacy Policy</h1>
          <p className="last-updated">Last Updated: March 2026</p>

          <p>
            Thank you for using <strong>Status Saver</strong>. This
            Privacy Policy explains how we collect, use, and protect your
            information when you use our{" "}
            <strong>Status Saver – Status Saver &amp; Downloader</strong>{" "}
            application ("the App"). We are committed to protecting your privacy
            and being fully transparent about our practices.
          </p>
          <p>
            By using the App, you agree to the collection and use of information
            as described in this policy. If you do not agree, please do not use
            the App.
          </p>

          <h2>1. About the App</h2>
          <p>
            Status Saver is a free, independent tool that allows users
            to save, view, and share status photos and videos from messaging
            apps. The App works entirely on your device using locally stored
            media files. It does not upload, transmit, or store any media to
            external servers.
          </p>
          <p>
            Status Saver is not affiliated with, endorsed by, or
            connected to WhatsApp LLC or any other third-party messaging
            platform. All trademarks belong to their respective owners.
          </p>

          <h2>2. Information We Collect</h2>

          <h3>2.1 Information We Do NOT Collect</h3>
          <p>
            We are committed to minimal data collection. Status Saver
            does <strong>NOT</strong> collect:
          </p>
          <ul>
            <li>Personal identification information (name, email, phone number)</li>
            <li>Login credentials or account information</li>
            <li>Contact lists or messaging data</li>
            <li>Location data</li>
            <li>Browsing history or activity logs</li>
          </ul>

          <h3>2.2 Information Collected Automatically</h3>
          <p>
            When you use the App, certain non-personal technical data may be
            automatically collected by third-party services we use (such as
            crash reporting or analytics tools). This may include:
          </p>
          <ul>
            <li>Device model and operating system version</li>
            <li>App version and configuration</li>
            <li>Anonymous usage statistics (e.g., app crashes, error logs)</li>
            <li>General device identifiers (non-personally identifiable)</li>
          </ul>
          <p>
            This data is used solely to improve app performance and fix
            technical issues. It cannot be used to identify you personally.
          </p>

          <h3>2.3 Information You Voluntarily Provide</h3>
          <p>
            If you contact us for support or feedback, you may voluntarily
            provide:
          </p>
          <ul>
            <li>Your name and email address</li>
            <li>Details of your inquiry or issue</li>
          </ul>
          <p>
            This information is used only to respond to your request and is
            never sold or shared with third parties.
          </p>

          <h2>3. How the App Works</h2>
          <p>
            Status Saver works by reading media files that messaging
            apps store in your device's local storage. When you view a
            contact's status in a messaging app, the media is temporarily
            cached on your device. Our App detects these cached files and
            displays them so you can choose to save them to your gallery.
          </p>
          <p>
            All processing happens entirely on your device. We do not access,
            upload, or transmit any of these media files to any server or third
            party.
          </p>

          <h2>4. Permissions We Request</h2>
          <p>
            To function properly, the App may request the following permissions:
          </p>

          <h3>4.1 Storage Permission</h3>
          <p>
            Required to read status media from your device's local storage and
            to save files to your gallery. We only access files in the specific
            folder where messaging apps store status media. We do not access any
            other files on your device.
          </p>

          <h3>4.2 Media Access (Android 13+)</h3>
          <p>
            On newer Android versions, the App requests access to photos and
            videos specifically to read status media and save files to your
            gallery. No other media on your device is accessed.
          </p>

          <h3>4.3 Internet Permission</h3>
          <p>
            The App may use the internet connection only for loading
            advertisements (if applicable) and for crash reporting to help us
            fix technical issues. The App's core functionality of saving status
            media works completely offline.
          </p>
          <p>We do not use any permission to collect or transmit personal data.</p>

          <h2>5. Third-Party Services</h2>
          <p>
            The App may use the following third-party services which have their
            own privacy policies:
          </p>

          <h3>5.1 Google Play Services</h3>
          <p>Used for in-app billing and core Android functionality.</p>
          <ul>
            <li>
              Privacy Policy:{" "}
              <a
                href="https://policies.google.com/privacy"
                target="_blank"
                rel="noopener noreferrer"
              >
                https://policies.google.com/privacy
              </a>
            </li>
          </ul>

          <h3>5.2 AdMob (Google Advertising)</h3>
          <p>
            Used to display advertisements in the free version of the App.
            AdMob may collect anonymous device identifiers for ad personalization
            purposes.
          </p>
          <ul>
            <li>
              Privacy Policy:{" "}
              <a
                href="https://support.google.com/admob/answer/6128543"
                target="_blank"
                rel="noopener noreferrer"
              >
                https://support.google.com/admob/answer/6128543
              </a>
            </li>
            <li>
              You can opt out of personalized ads in your device settings under
              Google &gt; Ads
            </li>
          </ul>

          <h3>5.3 Firebase Crashlytics</h3>
          <p>
            Used to collect anonymous crash reports and error logs to help us
            identify and fix technical issues.
          </p>
          <ul>
            <li>
              Privacy Policy:{" "}
              <a
                href="https://firebase.google.com/support/privacy"
                target="_blank"
                rel="noopener noreferrer"
              >
                https://firebase.google.com/support/privacy
              </a>
            </li>
          </ul>

          <h3>5.4 Google Analytics for Firebase</h3>
          <p>
            Used to collect anonymous usage statistics to help us understand how
            the App is used and improve the user experience.
          </p>
          <ul>
            <li>
              Privacy Policy:{" "}
              <a
                href="https://firebase.google.com/policies/analytics"
                target="_blank"
                rel="noopener noreferrer"
              >
                https://firebase.google.com/policies/analytics
              </a>
            </li>
          </ul>
          <p>
            These third-party services are bound by their own privacy policies.
            We recommend reviewing them to understand their data practices. We
            do not control and are not responsible for the content or practices
            of these third-party services.
          </p>

          <h2>6. How We Use Information</h2>
          <p>
            The limited information we collect is used only for the following
            purposes:
          </p>
          <ul>
            <li>To operate and improve the App's performance and reliability</li>
            <li>To identify and fix technical bugs and crashes</li>
            <li>To respond to user support requests and inquiries</li>
            <li>To display advertisements in the free version of the App</li>
            <li>To comply with legal obligations if required</li>
          </ul>
          <p>
            We do not sell, rent, trade, or share your personal information with
            any third party for their own marketing purposes.
          </p>

          <h2>7. Data Storage and Security</h2>
          <p>
            Status Saver does not maintain any server-side database of
            user data. All media files saved through the App remain exclusively
            on your device.
          </p>
          <p>
            For the limited technical data collected by third-party services
            (crash logs, analytics), we take reasonable measures to ensure it is
            handled securely. However, no method of electronic storage or
            transmission is 100% secure, and we cannot guarantee absolute
            security.
          </p>
          <p>
            If you become aware of any security concern related to the App,
            please contact us immediately at the email address provided below.
          </p>

          <h2>8. Data Retention</h2>
          <p>
            We do not store any personal data on our servers. Any data collected
            by third-party services (such as crash logs) is retained according
            to their respective data retention policies.
          </p>
          <p>
            If you contact us for support, we may retain your correspondence for
            as long as necessary to resolve your inquiry or as required by
            applicable law.
          </p>

          <h2>9. Children's Privacy</h2>
          <p>
            Status Saver is not intended for use by children under the
            age of 13. We do not knowingly collect personal information from
            children under 13.
          </p>
          <p>
            If you are a parent or guardian and believe your child has provided
            us with personal information, please contact us at{" "}
            <strong>contact@cousync.com</strong>. We will promptly delete any
            such information from our records.
          </p>

          <h3>Zero Tolerance Policy on CSAE:</h3>
          <ul>
            <li>Immediate removal and reporting of illegal content</li>
            <li>In-app/email reporting tools for users</li>
            <li>
              Cooperation with law enforcement and global child protection
              agencies
            </li>
          </ul>
          <p>
            For urgent reporting, contact us at:{" "}
            <strong>contact@cousync.com</strong>
          </p>

          <h2>10. User Rights</h2>
          <p>
            Depending on your location, you may have certain rights regarding
            your personal data, including:
          </p>
          <ul>
            <li>The right to access information we hold about you</li>
            <li>The right to request correction of inaccurate data</li>
            <li>The right to request deletion of your data</li>
            <li>The right to opt out of personalized advertising</li>
            <li>The right to lodge a complaint with a supervisory authority</li>
          </ul>
          <p>
            To exercise any of these rights, please contact us using the details
            provided below. We will respond to your request within 30 days.
          </p>

          <h2>11. Advertising</h2>
          <p>
            The free version of Status Saver may display advertisements
            provided by Google AdMob and other advertising partners. These
            advertisements help us keep the App free for all users.
          </p>
          <p>
            Advertising partners may use cookies and similar technologies to
            show you relevant ads based on your interests. You can opt out of
            personalized advertising at any time:
          </p>
          <ul>
            <li>
              On Android: Go to Settings &gt; Google &gt; Ads &gt; Opt out of
              Ads Personalization
            </li>
            <li>
              Or visit:{" "}
              <a
                href="https://www.aboutads.info/choices/"
                target="_blank"
                rel="noopener noreferrer"
              >
                https://www.aboutads.info/choices/
              </a>
            </li>
          </ul>
          <p>
            Opting out of personalized ads does not remove ads from the App —
            you will still see ads, but they will not be personalized to your
            interests.
          </p>

          <h2>12. Copyright and Content Responsibility</h2>
          <p>
            Status Saver is a tool that enables users to save media
            that is already stored on their own device. Users are solely
            responsible for how they use the App and the content they save or
            share.
          </p>
          <p>We strongly advise users to:</p>
          <ul>
            <li>
              Only save status content with the permission of the original
              creator
            </li>
            <li>
              Respect the intellectual property and privacy rights of others
            </li>
            <li>
              Not use the App to save or distribute content without authorization
            </li>
          </ul>
          <p>
            Status Saver does not condone the unauthorized downloading,
            sharing, or distribution of any content and is not responsible for
            any misuse of the App by its users.
          </p>

          <h2>13. Changes to This Privacy Policy</h2>
          <p>
            We may update this Privacy Policy from time to time to reflect
            changes in our practices, technology, or legal requirements. When we
            make changes, we will update the "Last Updated" date at the top of
            this policy.
          </p>
          <p>
            We encourage you to review this Privacy Policy periodically. Your
            continued use of the App after any changes constitutes your
            acceptance of the updated policy. For significant changes, we will
            notify you through the App or via a notification on the Play Store
            listing.
          </p>

          <h2>14. Contact Us</h2>
          <p>
            If you have any questions, concerns, or requests regarding this
            Privacy Policy or our data practices, please contact us:
          </p>
          <ul>
            <li>
              <strong>Email:</strong> contact@cousync.com
            </li>
            <li>
              <strong>Organization:</strong> CouSync LLC
            </li>
            <li>
              <strong>App Name:</strong> Status Saver – Status Saver
              &amp; Downloader
            </li>
            <li>
              <strong>Google Play:</strong> Available on Google Play Store
            </li>
          </ul>
          <p>
            We are committed to resolving any privacy concerns promptly and will
            respond to all inquiries within 30 days.
          </p>
        </div>

        <footer className="privacy-footer">
          All rights reserved © 2026 CouSync LLC
        </footer>
      </div>
    </>
  );
};

export default Privacy;
